import JSZip from 'jszip';
import { utils, write, WorkSheet } from 'xlsx';
import { SpeechNeed } from '../interfaces/Frameworks';
import { getSupportAreaImportValue } from './ClientsImport';
import { addClientsImportTemplateLogo, loadClientsImportTemplateLogo } from './ClientsImportTemplateLogo';

export type ClientsImportTemplateProvider = { name: string; logoUrl?: string };

const FIRST_DATA_ROW = 4;
const LAST_TEMPLATE_ROW = FIRST_DATA_ROW + 499;
const SUPPORT_AREA_RANGE_NAME = 'SupportAreaValues';
const COLUMN_WIDTH_SCALE = 0.6;
const CLIENT_HEADERS = [
    'First name',
    'Last name',
    'Date of birth',
    'Support area 1',
    'Support area 2',
    'Support area 3',
    'Notes',
    'First name',
    'Last name',
    'Phone number',
    'Email',
    'Notes',
    'Comms campaign',
    'First name',
    'Last name',
    'Phone number',
    'Email',
    'Notes',
    'Comms campaign',
];

// Apply the same 40% reduction to every column across the template's worksheets.
function templateColumnWidth(width: number): { wch: number } {
    return { wch: Number((width * COLUMN_WIDTH_SCALE).toFixed(1)) };
}

// Group the fields below the title, using calendar dates for DOB and text for other inputs.
function createClientsSheet(providerName: string, hasLogo: boolean): WorkSheet {
    const titleRow = CLIENT_HEADERS.map(() => '');
    titleRow[hasLogo ? 1 : 0] = `${providerName} clients for SuperPenguin`;
    const sectionRow = CLIENT_HEADERS.map(() => '');
    sectionRow[0] = 'CARE RECIPIENT';
    sectionRow[7] = 'CARER 1';
    sectionRow[13] = 'CARER 2';
    const rows = [titleRow, sectionRow, CLIENT_HEADERS,
        ...Array.from({ length: LAST_TEMPLATE_ROW - FIRST_DATA_ROW + 1 }, () => CLIENT_HEADERS.map(() => ''))];
    const worksheet = utils.aoa_to_sheet(rows);
    worksheet['!cols'] = CLIENT_HEADERS.map((header, index) => templateColumnWidth(
        index >= 3 && index <= 5 ? 32 : header === 'Notes' ? 34 : 24
    ));
    worksheet['!rows'] = [{ hpt: 72 }, { hpt: 30 }, { hpt: 36 }];
    worksheet['!merges'] = [
        utils.decode_range(hasLogo ? 'B1:S1' : 'A1:S1'),
        utils.decode_range('A2:G2'),
        utils.decode_range('H2:M2'),
        utils.decode_range('N2:S2'),
    ];

    for (let row = FIRST_DATA_ROW - 1; row < LAST_TEMPLATE_ROW; row += 1) {
        CLIENT_HEADERS.forEach((_, column) => {
            worksheet[utils.encode_cell({ r: row, c: column })].z = column === 2 ? 'yyyy-mm-dd' : '@';
        });
    }
    return worksheet;
}

// Explain the exact fill-and-upload workflow without putting example people in the data sheet.
function createInstructionsSheet(speechNeeds: SpeechNeed[]): WorkSheet {
    const hasSpeechNeeds = speechNeeds.length > 0;
    const rows = [
        ['Clients import template', 'Instructions'],
        ['1. Fill in Clients', 'Enter one care recipient per row on the Clients sheet, starting at row 4. Keep the title and both header rows (rows 1 to 3), and keep Clients as the first sheet. It contains no sample contacts.'],
        ['Required care recipient fields', 'Enter first name, last name, date of birth (YYYY-MM-DD), and at least one support area across the three support area columns. Notes can be left blank.'],
        ['Date of birth', 'Column C is formatted as a date, displayed as YYYY-MM-DD without a time. Enter a calendar date. In Numbers, this is the Date & Time format with Time set to None.'],
        ['2. Choose support areas', hasSpeechNeeds
            ? 'Use the dropdowns in Support area 1, Support area 2, and Support area 3 (columns D, E, and F). Choose one need per column. Leave unused columns blank.'
            : 'No support areas are available to your service. Ask your service administrator to enable them, then download a new template before importing.'],
        ['One, two, or three needs', 'Choose any additional needs in the other dropdown columns. The importer combines all non-empty selections and ignores duplicates. You do not need to type separators.'],
        ['Available choices', 'The Support areas sheet lists the needs available to your service. The Value to copy column shows the dropdown values. Some use an ID to distinguish names that are identical or contain separator punctuation.'],
        ['Carer details', 'Include at least one carer with first name, last name, and phone number. Email and notes are optional. Leave every Carer 2 field blank if there is no second carer.'],
        ['More carers', 'For another carer, add a Carer 3 section in row 2 with First name, Last name, Phone number, Email, Notes, and Comms campaign below it in row 3. Repeat for further carers.'],
        ['Phone numbers', 'UK phone numbers can use their usual UK format, including the initial 0. For other countries, include the country code, for example +353. Phone number cells are formatted as text.'],
        ['Comms campaign', 'Enter yes to enrol a carer in your service’s default communications campaign; enter no or leave blank to skip. A default campaign must be configured to use yes.'],
        ['3. Save and upload', 'Save the completed workbook as .xlsx, then go to Clients, open the ellipsis (…) menu next to New, and select Import. Choose your completed file and review the confirmation before importing.'],
        ['If a value is not recognised', 'Import validation identifies the row to correct. Check against this template’s Support areas list or download a fresh template if your service’s choices have changed.'],
        ['Blank rows', 'Rows with neither a care recipient first name nor last name are ignored. The first 500 entry rows are preformatted; you can add more rows if needed.'],
    ];
    const worksheet = utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [31, 105].map(templateColumnWidth);
    // Allow both the labels and instructions to wrap within the narrower columns.
    worksheet['!rows'] = rows.map((row, index) => ({
        hpt: index === 0 ? 32 : Math.max(54,
            Math.ceil(row[0].length / (30 * COLUMN_WIDTH_SCALE)) * 17 + 12,
            Math.ceil(row[1].length / (100 * COLUMN_WIDTH_SCALE)) * 17 + 12),
    }));
    return worksheet;
}

// Add the few Excel styles and validation features that SheetJS CE does not write.
function appendStyle(xml: string, tag: string, element: string): { xml: string; index: number } {
    const pattern = new RegExp(`<${tag} count="(\\d+)">([\\s\\S]*?)</${tag}>`);
    const match = xml.match(pattern);
    if (!match) {
        throw new Error('Unable to format the import template.');
    }
    const index = Number(match[1]);
    return {
        xml: xml.replace(pattern, `<${tag} count="${index + 1}">${match[2]}${element}</${tag}>`),
        index,
    };
}

// Set a known style on the generated cells while preserving their string values.
function styleCells(xml: string, styleIndex: number): string {
    return xml.replace(/<c\b([^>]*)>/g, (_, attributes: string) =>
        `<c${attributes.replace(/\s+s="\d+"/, '')} s="${styleIndex}">`
    );
}

// Create a fresh workbook using only the provider-visible catalogue passed by the caller.
export async function createClientsImportTemplate(
    speechNeeds: SpeechNeed[],
    provider: ClientsImportTemplateProvider
): Promise<Uint8Array> {
    const providerName = provider.name.trim();
    if (!providerName) {
        throw new Error('The healthcare provider name is not available yet. Please try again.');
    }
    const logo = await loadClientsImportTemplateLogo(provider.logoUrl);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, createClientsSheet(providerName, logo !== null), 'Clients');
    utils.book_append_sheet(workbook, createInstructionsSheet(speechNeeds), 'Instructions');

    const supportAreasSheet = utils.aoa_to_sheet([
        ['Support area', 'Value to copy'],
        ...speechNeeds.map((speechNeed) => [speechNeed.name, getSupportAreaImportValue(speechNeed, speechNeeds)]),
    ]);
    supportAreasSheet['!cols'] = [52, 52].map(templateColumnWidth);
    supportAreasSheet['!rows'] = [{ hpt: 32 }, ...speechNeeds.map((speechNeed) => ({
        hpt: Math.max(32, Math.ceil(Math.max(speechNeed.name.length, getSupportAreaImportValue(speechNeed, speechNeeds).length) / (50 * COLUMN_WIDTH_SCALE)) * 17 + 12),
    }))];
    utils.book_append_sheet(workbook, supportAreasSheet, 'Support areas');

    // The named range keeps the dropdown tied to the same copyable values as the reference sheet.
    if (speechNeeds.length > 0) {
        workbook.Workbook = {
            Names: [{ Name: SUPPORT_AREA_RANGE_NAME, Ref: `'Support areas'!$B$2:$B$${speechNeeds.length + 1}` }],
        };
    }

    const zip = await JSZip.loadAsync(write(workbook, { bookType: 'xlsx', type: 'array' }));
    let styles = await zip.file('xl/styles.xml')!.async('string');
    const font = appendStyle(styles, 'fonts', '<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>');
    const fill = appendStyle(font.xml, 'fills', '<fill><patternFill patternType="solid"><fgColor rgb="FF514B8C"/><bgColor indexed="64"/></patternFill></fill>');
    const referenceHeader = appendStyle(fill.xml, 'cellXfs', `<xf numFmtId="0" fontId="${font.index}" fillId="${fill.index}" borderId="0" xfId="0" applyAlignment="1" applyFont="1" applyFill="1"><alignment vertical="center" wrapText="1"/></xf>`);
    // Use the requested colours for the provider title and the two grouped header rows.
    const headerFill = appendStyle(referenceHeader.xml, 'fills', '<fill><patternFill patternType="solid"><fgColor rgb="FF807EDD"/><bgColor indexed="64"/></patternFill></fill>');
    const header = appendStyle(headerFill.xml, 'cellXfs', `<xf numFmtId="0" fontId="${font.index}" fillId="${headerFill.index}" borderId="0" xfId="0" applyAlignment="1" applyFont="1" applyFill="1"><alignment vertical="center" wrapText="1"/></xf>`);
    const titleFont = appendStyle(header.xml, 'fonts', '<font><b/><sz val="20"/><color rgb="FF000000"/><name val="Calibri"/></font>');
    const titleFill = appendStyle(titleFont.xml, 'fills', '<fill><patternFill patternType="solid"><fgColor rgb="FFE6E5FB"/><bgColor indexed="64"/></patternFill></fill>');
    const title = appendStyle(titleFill.xml, 'cellXfs', `<xf numFmtId="0" fontId="${titleFont.index}" fillId="${titleFill.index}" borderId="0" xfId="0" applyAlignment="1" applyFont="1" applyFill="1"><alignment vertical="center" indent="1" wrapText="1"/></xf>`);
    const body = appendStyle(title.xml, 'cellXfs', '<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>');
    styles = body.xml;
    zip.file('xl/styles.xml', styles);

    // Freeze the data headers and offer the same provider choices in all three columns.
    for (let sheetIndex = 1; sheetIndex <= 3; sheetIndex += 1) {
        const path = `xl/worksheets/sheet${sheetIndex}.xml`;
        let xml = await zip.file(path)!.async('string');
        if (sheetIndex !== 1) {
            xml = styleCells(xml, body.index);
            xml = xml.replace(/<row r="1"[^>]*>[\s\S]*?<\/row>/, (row) => styleCells(row, referenceHeader.index));
        }
        if (sheetIndex === 1) {
            // Keep DOB cells truly blank with a date style, rather than storing empty text values.
            xml = xml.replace(/<c r="C(\d+)"([^>]*)><v><\/v><\/c>/g, (cell, rowNumber: string, attributes: string) => (
                Number(rowNumber) >= FIRST_DATA_ROW && Number(rowNumber) <= LAST_TEMPLATE_ROW
                    ? `<c r="C${rowNumber}"${attributes.replace(/\s+t="str"/, '')}/>`
                    : cell
            ));
            xml = xml.replace(/<row r="1"[^>]*>[\s\S]*?<\/row>/, (row) => styleCells(row, title.index));
            xml = xml.replace(/<row r="[23]"[^>]*>[\s\S]*?<\/row>/g, (row) => styleCells(row, header.index));
            xml = xml.replace(/<sheetViews>[\s\S]*?<\/sheetViews>/, '<sheetViews><sheetView workbookViewId="0"><pane ySplit="3" topLeftCell="A4" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="A4" sqref="A4"/></sheetView></sheetViews>');
            if (speechNeeds.length > 0) {
                const validation = `<dataValidations count="1"><dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="0" promptTitle="Support areas" prompt="Choose one need per column. Use Support area 1, 2 and 3 for multiple needs. Leave unused columns blank." sqref="D${FIRST_DATA_ROW}:F${LAST_TEMPLATE_ROW}"><formula1>${SUPPORT_AREA_RANGE_NAME}</formula1></dataValidation></dataValidations>`;
                // Excel requires validation rules to follow the merged-cell definitions.
                xml = xml.replace('</mergeCells>', `</mergeCells>${validation}`);
            }
        }
        zip.file(path, xml);
    }
    if (logo) {
        // Keep the image inside the reduced first column without covering the provider title.
        await addClientsImportTemplateLogo(zip, logo, 80);
    }

    return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

// Download the generated workbook through the browser without retaining its temporary URL.
export async function downloadClientsImportTemplate(
    speechNeeds: SpeechNeed[],
    provider: ClientsImportTemplateProvider
): Promise<void> {
    const bytes = await createClientsImportTemplate(speechNeeds, provider);
    const url = URL.createObjectURL(new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'clients-import-template.xlsx';
    document.body.appendChild(link);
    try {
        link.click();
    } finally {
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
}
